/* eslint-disable */
import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  useEditor,
  EditorContent,
  BubbleMenu,
} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextStyle from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import {
  faBold,
  faItalic,
  faStrikethrough,
  faHeading,
  faListUl,
  faListOl,
  faUndo,
  faRedo,
  faRemoveFormat,
  faHighlighter,
  faUnderline,
  faLink,
} from '@fortawesome/free-solid-svg-icons';
import { HexAlphaColorPicker } from 'react-colorful';
import ThemeContext from '../../../../theme/ThemeContext';
import Icon from '../../../misc/Icon';

const inputStyles = (props) => {
  return `
    overflow-y: auto;
    height: 100%;
    background-color: ${props.backgroundColor}!important;
    border-radius: ${props.borderRadius}px;
    border: ${props.borderWidth}px solid ${props.borderColor};
    padding: ${props.padding}px;
    resize: none;
    transition: border 0.15s ease-in-out;
    font-size:${props.fontSize}px !important;
    font-style: ${props.fontStyle};
    font-weight: ${props.fontWeight};
    text-align: ${props.textAlign};
    text-transform: ${props.textTransform};
    text-decoration: ${props.textDecoration};
    ${props.textOverflow ? `text-overflow: ${props.textOverflow};` : ''}
    line-height: ${props.lineHeightPixels !== undefined ? `${props.lineHeightPixels}px` : 'initial'} !important;
    background-color: transparent;


    &:focus, &:focus-visible {
      outline: unset;
      border: ${props.borderWidth}px solid ${props.theme.primaryColor};
    }

    &:hover {
      ${props.disabled ? '' : `border: ${props.borderWidth}px solid ${props.theme.primaryColor}`};
    }
  `;
};

const HeadingDropdown = styled.div`
    position: absolute;
    top: 34px;
    left: 39%;
    background-color: ${(props) => props.theme.backgroundColor || '#fff'};
    border: 1px solid ${(props) => props.theme.borderColor || '#e0e0e0'};
    border-radius: 6px;
    padding: 8px;
    z-index: 20000;
    display: flex;
    flex-direction: column;
    gap: 4px;
    z-index: 20000;

    button {
      display: flex;
      align-items: center;
      padding: 4px 6px;
      border: none;
      background: transparent;
      border-radius: 4px;

      &:hover {
        background-color: ${(props) => props.theme.secondaryColor || '#f7f7f7'};
        cursor: pointer;
      }
      &.is-active {
        background-color: ${(props) => props.theme.primaryColor || '#dcdcdc'};
        color: ${(props) => props.theme.backgroundColor || '#333'};
      }
    }
`;

const ColorPickerPopover = styled.div`
  position: absolute;
  top: 34px;
  right: 0;
  background-color: ${(props) => props.theme.backgroundColor || '#fff'};
  border: 1px solid ${(props) => props.theme.borderColor || '#e0e0e0'};
  border-radius: 6px;
  padding: 8px;
  z-index: 20000;

  .react-colorful {
    width: 140px;
    height: 120px;
  }
  .react-colorful__pointer {
    width: 20px !important;
    height: 20px !important;
  }
`;

const TiptapInput = styled.div`
  height: 100%;
  overflow-y: auto;
  .tiptap.ProseMirror {
    ${(props) => inputStyles(props)}
  }

  // Important as Tiptap adhears to html rules of p tags and h tags
  // so to line up with other inputs we need to remove the margin
  .tiptap.ProseMirror > *:first-child {
    margin-top: 4px !important;
  }
`;

const BubbleMenuContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: ${(props) => props.theme.backgroundColor || '#fff'};
  border: 1px solid ${(props) => props.theme.borderColor || '#e0e0e0'};
  border-radius: ${(props) => props.theme.borderRadius || 8}px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  padding: 0.4rem;
`;

const StyledButton = styled.button`
  border: none;
  background-color: transparent;
  border-radius: 4px;
  color: ${(props) => props.theme.textColor || '#333'};
  padding: 6px;
  font-size: 14px;

  &:hover {
    background-color: ${(props) => props.theme.secondaryColor || '#f7f7f7'};
    cursor: pointer;
  }
  &.is-active {
    background-color: ${(props) => props.theme.primaryColor || '#dcdcdc'};
    color: ${(props) => props.theme.backgroundColor || '#333'};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export default function TiptapEditor({
  content,
  disabled,
  onUpdate,
  onFocus,
  onBlur,
  hideMenu,

  borderColor,
  borderRadius,
  borderWidth,
  backgroundColor,
  padding,

  fontSize,
  fontStyle,
  fontWeight,
  textAlign,
  textTransform,
  textDecoration,
  textOverflow,
  lineHeight
}) {
  const theme = useContext(ThemeContext);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [headingDropdownOpen, setHeadingDropdownOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState('#115DF1');

  const editor = useEditor({
    content,
    editable: !disabled,
    extensions: [
      StarterKit.configure({
        bulletList: { keepMarks: true, keepAttributes: false },
        orderedList: { keepMarks: true, keepAttributes: false },
      }),
      TextStyle,
      Color,
      Underline,
      Link.configure({ openOnClick: true }),
    ],
    onUpdate: ({ editor }) => {
      if (onUpdate) {
        onUpdate(editor.getJSON());
      }
    },
    onFocus: () => {
      if (onFocus) {
        onFocus();
      }
    },
    onBlur: ({ editor }) => {
      if (onBlur) {
        onBlur(editor.getJSON());
      }
    },
  });

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  if (!editor) return null;

  const toggleColorPicker = () => setColorPickerOpen(!colorPickerOpen);

  return (
    <TiptapInput
      theme={theme}
      {...{
        borderColor,
        borderRadius,
        borderWidth,
        backgroundColor,
        padding,
        fontSize,
        fontStyle,
        fontWeight,
        textAlign,
        textTransform,
        textDecoration,
        textOverflow,
        lineHeightPixels: lineHeight
      }}
    >
      <BubbleMenu editor={editor} tippyOptions={{ duration: 100, maxWidth: 384, zIndex: 52000, }}>
        {!hideMenu && (
        <BubbleMenuContainer theme={theme}>
          <StyledButton
            theme={theme}
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={editor.isActive('bold') ? 'is-active' : ''}
          >
            <Icon icon={faBold} />
          </StyledButton>
          <StyledButton
            theme={theme}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={editor.isActive('italic') ? 'is-active' : ''}
          >
            <Icon icon={faItalic} />
          </StyledButton>
          <StyledButton
            theme={theme}
            onClick={() => editor.chain().focus().toggleStrike().run()}
            className={editor.isActive('strike') ? 'is-active' : ''}
          >
            <Icon icon={faStrikethrough} />
          </StyledButton>
          <StyledButton
            theme={theme}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={editor.isActive('underline') ? 'is-active' : ''}
          >
            <Icon icon={faUnderline} />
          </StyledButton>
          <StyledButton
            theme={theme}
            onClick={() => {
              const url = window.prompt('Enter the URL');
              if (url) {
                editor.chain().focus().setLink({ href: url }).run();
              } else {
                editor.chain().focus().unsetLink().run();
              }
            }}
            className={editor.isActive('link') ? 'is-active' : ''}
          >
            <Icon icon={faLink} />
          </StyledButton>
          <StyledButton
            theme={theme}
            onClick={() => setHeadingDropdownOpen(!headingDropdownOpen)}
            className={editor.isActive('heading') ? 'is-active' : ''}
            style={{ position: 'relative' }}
          >
            <Icon icon={faHeading} />
          </StyledButton>
          {headingDropdownOpen && (
          <HeadingDropdown theme={theme}>
            {[1, 2, 3, 4, 5, 6].map((lvl) => (
              <button
                key={`heading-${lvl}`}
                onClick={() => {
                  editor.chain().focus().toggleHeading({ level: lvl }).run();
                  setHeadingDropdownOpen(false);
                }}
                className={editor.isActive('heading', { level: lvl }) ? 'is-active' : ''}
              >
                H
                {lvl}
              </button>
            ))}
          </HeadingDropdown>
          )}
          <StyledButton
            theme={theme}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={editor.isActive('bulletList') ? 'is-active' : ''}
          >
            <Icon icon={faListUl} />
          </StyledButton>
          <StyledButton
            theme={theme}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={editor.isActive('orderedList') ? 'is-active' : ''}
          >
            <Icon icon={faListOl} />
          </StyledButton>
          <StyledButton
            theme={theme}
            onClick={() => editor.chain().focus().unsetAllMarks().run()}
          >
            <Icon icon={faRemoveFormat} />
          </StyledButton>
          <StyledButton
            theme={theme}
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().chain().focus().undo()
              .run()}
          >
            <Icon icon={faUndo} />
          </StyledButton>
          <StyledButton
            theme={theme}
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().chain().focus().redo()
              .run()}
          >
            <Icon icon={faRedo} />
          </StyledButton>
          <StyledButton
            theme={theme}
            onClick={toggleColorPicker}
            className={
              editor.isActive('textStyle', { color: selectedColor })
                ? 'is-active'
                : ''
            }
          >
            <Icon icon={faHighlighter} />
          </StyledButton>

          {colorPickerOpen && (
          <ColorPickerPopover theme={theme}>
            <HexAlphaColorPicker
              color={selectedColor}
              onChange={(val) => {
                setSelectedColor(val);
                editor.chain().focus().setColor(val).run();
              }}
            />
          </ColorPickerPopover>
          )}
        </BubbleMenuContainer>
        )}
      </BubbleMenu>

      {/* Actual editor field - keep in mind they do not render an <input> */}
      <EditorContent
        style={{ height: '100%' }}
        editor={editor}
      />
    </TiptapInput>
  );
}

TiptapEditor.propTypes = {
  content: PropTypes.string,
  disabled: PropTypes.bool,
  onUpdate: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,

  borderColor: PropTypes.string,
  borderRadius: PropTypes.number,
  borderWidth: PropTypes.number,
  backgroundColor: PropTypes.string,
  padding: PropTypes.number,
  fontSize: PropTypes.number,
  fontStyle: PropTypes.string,
  fontWeight: PropTypes.string,
  textAlign: PropTypes.string,
  textTransform: PropTypes.string,
  textDecoration: PropTypes.string,
  textOverflow: PropTypes.string,
};
